import React, { useState, useRef, useEffect } from 'react';
import { Coins, RotateCcw, Sparkles } from 'lucide-react';
import { usePremiumPacks } from '../hooks/usePremiumPacks';

interface SlotMachineProps {
  showStubHubAd: boolean;
  onSpin: () => void;
  onCoinsChange: (coins: number) => void;
}

type Symbol = '🍒' | '🍋' | '🍊' | '7️⃣' | '💎' | '⚡️';

const SYMBOLS: readonly Symbol[] = ['🍒', '🍋', '🍊', '7️⃣', '💎', '⚡️'];

const SYMBOL_WEIGHTS: Record<Symbol, number> = {
  '🍒': 80,
  '🍋': 30,
  '🍊': 15,
  '7️⃣': 1,
  '💎': 0,
  '⚡️': 0,
} as const;

const PREMIUM_SYMBOL_WEIGHTS: Record<Symbol, number> = {
  '🍒': 80,
  '🍋': 30,
  '🍊': 15,
  '7️⃣': 1,
  '💎': 2,
  '⚡️': 0.5,
} as const;

const WINNING_COMBINATIONS = {
  '🍒🍒🍒': 50,
  '🍋🍋🍋': 100,
  '🍊🍊🍊': 200,
  '7️⃣7️⃣7️⃣': 777,
  '💎💎💎': 1000,
  '⚡️⚡️⚡️': 3000,
} as const;

const SPIN_COST = 10;
const INITIAL_COINS = 100;

export function SlotMachine({ showStubHubAd, onSpin, onCoinsChange }: SlotMachineProps) {
  const [reels, setReels] = useState<Symbol[]>(['🍒', '🍒', '🍒']);
  const [spinning, setSpinning] = useState(false);
  const [win, setWin] = useState(0);
  const [spinCount, setSpinCount] = useState(0);
  const spinIntervalRef = useRef<NodeJS.Timeout>();
  const spinTimeoutRef = useRef<NodeJS.Timeout>();
  const [coins, setCoins] = useState(INITIAL_COINS);
  const [mobileAdIndex, setMobileAdIndex] = useState(0);
  const [usePremiumSpin, setUsePremiumSpin] = useState(false);
  const [ipCheckComplete, setIpCheckComplete] = useState(false);
  const [ipLimitMessage, setIpLimitMessage] = useState('');
  const [canRefresh, setCanRefresh] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const { packs, usePack } = usePremiumPacks();

  // Check IP-based coin limit on component mount
  useEffect(() => {
    const checkIPLimit = async () => {
      try {
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-ip-coins`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error('Failed to check IP limit, granting default coins');
          setCoins(INITIAL_COINS);
          onCoinsChange(INITIAL_COINS);
          setIpCheckComplete(true);
          return;
        }

        const data = await response.json();

        // Set coins to current balance from server
        setCoins(data.currentBalance);
        onCoinsChange(data.currentBalance);
        setCanRefresh(data.canRefresh || false);
        setRefreshCount(data.refreshCount || 0);

        // Show appropriate message based on status
        if (data.currentBalance === 0 && data.canRefresh) {
          setIpLimitMessage('');
        } else if (data.currentBalance === 0) {
          setIpLimitMessage('No coins remaining! Come back tomorrow for more free coins or purchase a premium pack.');
        } else if (data.remainingCoins === 0) {
          setIpLimitMessage('Daily limit reached! You can still use your remaining coins.');
        }

        setIpCheckComplete(true);
      } catch (error) {
        console.error('Error checking IP limit, granting default coins:', error);
        setCoins(INITIAL_COINS);
        onCoinsChange(INITIAL_COINS);
        setIpCheckComplete(true);
      }
    };

    checkIPLimit();
  }, [onCoinsChange]);

  // Update parent component when coins change
  useEffect(() => {
    onCoinsChange(coins);
  }, [coins, onCoinsChange]);

  // Spacebar triggers spin
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        spinReels();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [spinning, coins, usePremiumSpin]);

  // Rotate mobile ads every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMobileAdIndex(prev => (prev + 1) % 3);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const getRandomSymbol = (isPremium: boolean = false): Symbol => {
    const weights = isPremium ? PREMIUM_SYMBOL_WEIGHTS : SYMBOL_WEIGHTS;
    let totalWeight = 0;
    for (const weight of Object.values(weights)) {
      totalWeight += weight;
    }

    let random = Math.floor(Math.random() * totalWeight);

    for (const [symbol, weight] of Object.entries(weights)) {
      random -= weight;
      if (random < 0) {
        return symbol as Symbol;
      }
    }

    return '🍒';
  };

  const spinReels = async () => {
    if (spinning || coins < SPIN_COST) return;

    if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
    if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);

    const isPremiumSpin = usePremiumSpin;
    if (isPremiumSpin) {
      const packUsed = usePack('luckySpins');
      if (!packUsed) {
        setUsePremiumSpin(false);
        return;
      }
    }

    setSpinning(true);
    setWin(0);
    setSpinCount(prev => prev + 1);
    onSpin();

    // Optimistically deduct coins in UI
    setCoins(prev => prev - SPIN_COST);

    spinIntervalRef.current = setInterval(() => {
      setReels(prev => prev.map(() => getRandomSymbol(isPremiumSpin)));
    }, 100);

    spinTimeoutRef.current = setTimeout(async () => {
      if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);

      const finalReels = Array.from({ length: 3 }, () => getRandomSymbol(isPremiumSpin));
      setReels(finalReels);
      setSpinning(false);
      setUsePremiumSpin(false);

      const combination = finalReels.join('');
      const winAmount = WINNING_COMBINATIONS[combination as keyof typeof WINNING_COMBINATIONS] || 0;

      if (winAmount) {
        setWin(winAmount);
        setCoins(prev => prev + winAmount);
      }

      // Update balance on server
      try {
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/deduct-coins`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: SPIN_COST,
            winAmount: winAmount,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Failed to update balance:', error);
          // Sync with server balance if there's a mismatch
          if (error.currentBalance !== undefined) {
            setCoins(error.currentBalance);
          }
        } else {
          const data = await response.json();
          // Sync with server balance to ensure consistency
          setCoins(data.newBalance);

          // Check if they can now refresh (balance is 0)
          if (data.newBalance === 0 && refreshCount < 1) {
            setCanRefresh(true);
          }
        }
      } catch (error) {
        console.error('Error updating balance:', error);
      }
    }, 2000);
  };

  const handleRefreshCoins = async () => {
    if (refreshing || !canRefresh) return;

    setRefreshing(true);

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refresh-coins`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to refresh coins:', error);
        setIpLimitMessage(error.error || 'Unable to refresh coins');
        setRefreshing(false);
        return;
      }

      const data = await response.json();
      setCoins(data.newBalance);
      onCoinsChange(data.newBalance);
      setCanRefresh(false);
      setRefreshCount(data.refreshCount);
      setIpLimitMessage('');
    } catch (error) {
      console.error('Error refreshing coins:', error);
      setIpLimitMessage('Unable to refresh coins. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const canSpinNow = () => {
    return !spinning && coins >= SPIN_COST;
  };

  return (
    <div className="flex flex-col items-center justify-center gap-2 md:gap-6 p-3 md:p-8 max-w-4xl mx-auto w-full">
      <div className="text-4xl md:text-5xl font-bold text-yellow-500 font-serif tracking-wider mb-0 md:mb-2 text-center">Ten to Spin</div>
      <div className="text-base md:text-lg text-yellow-300 mb-2 md:mb-4 text-center">The Free Slot Machine Game</div>
      
      <div className="flex flex-col items-center gap-3">
        {!ipCheckComplete ? (
          <div className="flex items-center gap-2 bg-black/40 px-5 md:px-6 py-3 md:py-3 rounded-full shadow-lg border border-yellow-500/20">
            <span className="text-yellow-500 text-base">Checking eligibility...</span>
          </div>
        ) : (
          <div className="flex items-center gap-3 md:gap-4 bg-black/40 px-5 md:px-6 py-3 md:py-3 rounded-full shadow-lg border border-yellow-500/20">
            <Coins className="text-yellow-500 w-6 h-6 md:w-6 md:h-6" />
            <span className="text-xl md:text-xl text-white font-medium">{coins} coins</span>
          </div>
        )}

        {canRefresh && coins === 0 && (
          <button
            onClick={handleRefreshCoins}
            disabled={refreshing}
            className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-bold text-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg border-2 border-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refreshing ? 'Refreshing...' : 'Get 100 More Coins (1 Free Refresh)'}
          </button>
        )}

        {ipLimitMessage && (
          <div className="bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-base md:text-base text-center max-w-md">
            {ipLimitMessage}
          </div>
        )}

        {packs.luckySpins > 0 && (
          <button
            onClick={() => setUsePremiumSpin(!usePremiumSpin)}
            disabled={spinning}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all ${
              usePremiumSpin
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-base">Premium Spin ({packs.luckySpins} left)</span>
          </button>
        )}
      </div>

      <div className="bg-gradient-to-b from-green-900 to-green-800 p-4 md:p-10 rounded-2xl shadow-2xl border-2 border-yellow-500 relative overflow-hidden w-full slot-container">
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
        <div className="relative">
          <div className="flex justify-center gap-3 md:gap-4 mb-4 md:mb-8 p-4 md:p-8 bg-black rounded-xl border-2 border-yellow-300/50 shadow-inner">
            {reels.map((symbol, index) => (
              <div
                key={index}
                className={`w-24 h-32 md:w-32 md:h-40 flex items-center justify-center text-6xl md:text-6xl bg-gradient-to-b from-white to-gray-100 rounded-lg shadow-xl transform ${
                  spinning ? 'animate-bounce' : ''
                } transition-all duration-300`}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                {symbol}
              </div>
            ))}
          </div>

          <button
            onClick={spinReels}
            disabled={!canSpinNow()}
            className={`w-full py-4 md:py-4 px-5 md:px-6 rounded-xl text-white font-bold text-lg md:text-lg transition-all duration-300 transform hover:scale-105 ${
              !canSpinNow()
                ? 'bg-gray-700 cursor-not-allowed opacity-70'
                : 'bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 border-2 border-yellow-500 shadow-lg'
            }`}
          >
            <div className="flex items-center justify-center gap-3 md:gap-3">
              <RotateCcw className={`w-6 h-6 md:w-6 md:h-6 ${spinning ? 'animate-spin' : ''}`} />
              {spinning ? 'Spinning...' : `Spin (${SPIN_COST} coins)`}
            </div>
          </button>
        </div>
      </div>

      {win > 0 && (
        <div className="flex flex-col items-center gap-2 mt-4">
          <div className="text-2xl md:text-3xl font-bold text-green-500 animate-bounce bg-black/30 px-6 md:px-8 py-4 md:py-4 rounded-full border border-green-500/30 shadow-lg">
            You won {win} coins! 🎉
          </div>
        </div>
      )}

      {/* Mobile Rotating Ads */}
      <div className="md:hidden flex justify-center items-start py-0">
        <div className="flex items-center justify-center">
          {mobileAdIndex === 0 ? (
            <a href="https://Noir21.com" target="_blank" rel="noopener noreferrer" className="rounded-lg shadow-lg overflow-hidden block">
              <img src="/IMG_7907.JPG" width="120" height="240" alt="Noir21" className="object-cover hover:opacity-90 transition-opacity" />
            </a>
          ) : mobileAdIndex === 1 ? (
            <a href="https://shop.ledger.com/pages/ledger-nano-s-plus/?r=d887e180e2a0" target="_blank" rel="noopener noreferrer" className="block">
              <img width="200" height="80" src="https://affiliate.ledger.com/image/250/100/Default" alt="Ledger Nano S Plus" className="rounded shadow-lg" />
            </a>
          ) : (
            <a href="https://briabackwoods.com" target="_blank" rel="noopener noreferrer" className="rounded-lg shadow-lg overflow-hidden block">
              <img src="/bb.jpeg" width="160" height="240" alt="Bria Backwoods" className="object-cover hover:opacity-90 transition-opacity" />
            </a>
          )}
        </div>
      </div>

      <div className="text-xs md:text-sm text-white mt-4 md:mt-6 w-full max-w-2xl px-2 md:px-4">
        <div className="font-bold mb-3 md:mb-4 text-yellow-500 text-center text-lg md:text-xl">Winning Combinations</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {Object.entries(WINNING_COMBINATIONS).sort((a, b) => b[1] - a[1]).map(([combo, prize]) => (
            <div
              key={combo}
              className="flex items-center justify-between bg-black/40 p-2.5 md:p-3 rounded-lg border border-yellow-500/20 hover:border-yellow-500/40 transition-colors backdrop-blur-sm"
            >
              <div className="text-base md:text-lg">{combo}</div>
              <span className="text-yellow-400 font-bold text-sm md:text-base">{prize}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}